import {Component, OnInit, signal, inject, ChangeDetectionStrategy} from '@angular/core';
import {ReactiveFormsModule} from "@angular/forms";
import { lastValueFrom } from 'rxjs';

import { EmailInputComponent } from '../email-input/email-input.component';
import { OtpInputComponent } from '../otp-input/otp-input.component';
import { Router } from '@angular/router';
import { UsersService } from '../../../shared/services/users/users.service';
import { ErrorHandlingService, UserFriendlyError } from '../../../shared/services/error-handling.service';
import { JwtUser } from '../../../shared/models/user';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    ReactiveFormsModule,
    EmailInputComponent,
    OtpInputComponent
]
})
export class LoginComponent implements OnInit{
  otpSent = signal<boolean>(false);
  userEmail: string = '';
  sessionInfo: string | null = null;
  isLoading = signal<boolean>(false);
  errorMessage: string | null = null;
  currentError: UserFriendlyError | null = null;
  loadingMessage: string = '';
  shouldShowRetry: boolean = false;
  private lastAction: 'email' | 'otp' | null = null;
  private lastActionData: any = null;

  isBetaEnvironment = signal<boolean>(false);
  isOldDomain = signal<boolean>(false);

  private usersService = inject(UsersService);
  private router = inject(Router);
  private errorHandlingService = inject(ErrorHandlingService);

  ngOnInit(): void {
    const hostname = window.location.hostname;
    this.isBetaEnvironment.set(hostname.includes('beta'));
    this.isOldDomain.set(hostname.includes('sillion'));
  }

  handleEmailSubmit(email: string) {
    this.userEmail = email;
    this.lastAction = 'email';
    this.lastActionData = email;
    this.isLoading.set(true);
    this.loadingMessage = 'Enviando código de verificación...';
    this.clearError();

    this.usersService.login(this.userEmail).subscribe(response => { 
      this.sessionInfo = response?.session || null;

      if (this.sessionInfo) {
        this.otpSent.set(true);
        this.showSuccessMessage('Código enviado exitosamente a su email');
      } else {
        this.showError({
          title: 'Error de sesión',
          message: 'No hay información de sesión disponible. Por favor, solicite un nuevo código.',
          type: 'error'
        });
      }

      this.isLoading.set(false);
    });
  }

  handleOtpSubmit(otp: string) {
    if (!this.sessionInfo) {
      console.error('No session info available');
      this.showError({
        title: 'Error de sesión',
        message: 'No hay información de sesión disponible. Por favor, solicite un nuevo código.',
        type: 'error'
      });
      return;
    }

    this.lastAction = 'otp';
    this.lastActionData = otp;
    this.isLoading.set(true);
    this.loadingMessage = 'Verificando código...';
    this.clearError();

    this.usersService.verifyOtp(this.userEmail, otp, this.sessionInfo).subscribe(response => {
      if(response.accessToken) {
        const user: JwtUser = this.usersService.decodeToken(response.idToken);
        this.usersService.setUser(response.idToken);
        this.usersService.setToken(response.accessToken);
        this.usersService.setLoggedIn(true);

        this.router.navigate(["/redirect"]);
      } else {
        this.handleError("error verifyng OTP");
      }
      this.isLoading.set(false);
    });
  }

  handleBackToEmail(): void {
    this.otpSent.set(false);
    this.userEmail = '';
    this.sessionInfo = null;
    this.clearError();
  }

  private handleError(error: any): void {
    const userError = this.errorHandlingService.handleError(error);
    this.showError(userError);
    this.shouldShowRetry = this.errorHandlingService.shouldRetry(error);
  }

  private showError(error: UserFriendlyError): void {
    this.currentError = error;
    this.errorMessage = error.message;
  }

  private showSuccessMessage(message: string): void {
    this.currentError = {
      title: 'Éxito',
      message: message,
      type: 'info'
    };
    this.errorMessage = message;

    if (this.currentError?.type === 'info') {
      this.clearError();
    }
  }

  clearError(): void {
    this.errorMessage = null;
    this.currentError = null;
    this.shouldShowRetry = false;
  }

  retryLastAction(): void {
    if (!this.lastAction || this.isLoading()) {
      return;
    }

    this.clearError();

    switch (this.lastAction) {
      case 'email':
        if (this.lastActionData) {
          this.handleEmailSubmit(this.lastActionData);
        }
        break;
      case 'otp':
        if (this.lastActionData) {
          this.handleOtpSubmit(this.lastActionData);
        }
        break;
    }
  }

  formatErrorMessage(message: string): string {
    return message.replace(/\n/g, '<br>');
  }
}
